import React, { useEffect, useState } from "react";
import { useReportContext } from "../../../../_context/ReportContext";
import { useUserContext } from "../../../../_context/UserContext";
import { Area } from "../../../../_models/Report.model";
import { ClientType } from "../../../../_models/Client.model";
import { ClientSubAreas } from "./ClientSubAreas";
import "../../../Admin/AddUsers/AddUsers.css";
import "../../Reports.css";

/* ── SVG Icons ── */
const ThumbsUpIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const ThumbsDownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

export const ClientAreaBasedScoringView = () => {
  const { newReport, setNewReport, setViewedAreas } = useReportContext();
  const { loggedUser } = useUserContext();
  const [areaBasedScoringAreabuttonsClicked, setAreaBasedScoringAreabuttonsClicked] = useState<{ [key: number]: string | null }>({});
  const [isNewReport, setIsNewReport] = useState(true);

  const handleAreaItemScores = (area: Area, score: number): Area => {
    const updatedArea = { ...area };
    updatedArea.SubAreas = area.SubAreas.map((subArea) => ({
      ...subArea,
      Items: subArea.Items.map((item) => ({ ...item, Mark: score })),
    }));
    return updatedArea;
  };

  useEffect(() => {
    if (loggedUser?.data.Name && newReport.InspectedBy !== loggedUser.data.Name) {
      setNewReport((prev) => ({ ...prev, InspectedBy: loggedUser!.data.Name }));
    }
  }, [loggedUser, newReport.InspectedBy, setNewReport])

  // Initialize button click state from context when component mounts
  useEffect(() => {
    const buttonState: { [key: number]: string | null } = {};
    const alreadyViewedAreas = new Set<string>();
    newReport.Areas.forEach((area, areaIndex) => {
      // Check if all items in this area have the same score
      const firstItemScore = area.SubAreas[0]?.Items[0]?.Mark;
      if (firstItemScore && firstItemScore > 0) {
        // All items should have the same score in area-based scoring
        const allSameScore = area.SubAreas.every(subArea =>
          subArea.Items.every(item => item.Mark === firstItemScore)
        );
        if (allSameScore) {
          buttonState[areaIndex] = firstItemScore === 5 ? "up" : "down";
          alreadyViewedAreas.add(area.Name);
        }
      }
    });
    setAreaBasedScoringAreabuttonsClicked(buttonState);
    // Mark already-scored areas as viewed so navigation isn't blocked
    if (alreadyViewedAreas.size > 0) {
      setViewedAreas(prev => {
        const next = new Set(prev);
        alreadyViewedAreas.forEach(name => next.add(name));
        return next;
      });
    }
    setIsNewReport(false);
  }, [newReport.Areas]);

  useEffect(() => {
    // Reset button clicks state when a new report is selected
    if (newReport && isNewReport) {
      setAreaBasedScoringAreabuttonsClicked({});
    }
  }, [newReport, isNewReport]);

  const handleButtonClick = (areaIndex: number, buttonName: string) => {
    setAreaBasedScoringAreabuttonsClicked((prev) => ({
      ...prev,
      [areaIndex]: prev[areaIndex] === buttonName ? null : buttonName,
    }));

    setIsNewReport(false);

    // Mark this area as viewed so navigation validation passes
    setViewedAreas(prev => new Set(prev).add(newReport.Areas[areaIndex].Name));

    setNewReport((prevReport) => {
      const updatedReport = { ...prevReport };
      updatedReport.Areas = prevReport.Areas.map((area, index) => {
        if (index === areaIndex) {
          return handleAreaItemScores(area, buttonName === "up" ? 5 : 1);
        }
        return area;
      });
      return updatedReport;
    });
  };

  return (
    <div className="reports-page-wrapper" style={{ maxWidth: 900 }}>
      {/* Premium Header */}
      <div className="rp-client-header" style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 className="rp-client-title">Area Scoring</h2>
          <div className="rp-client-email" style={{ marginTop: 4 }}>
            Assess each area as strictly Pass or Fail
          </div>
        </div>

        {newReport.Client.Type !== ClientType.NDIS && (
          <div style={{ background: "rgba(255,255,255,0.2)", padding: "12px 24px", borderRadius: "12px", textAlign: "center" }}>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "rgba(255,255,255,0.9)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px" }}>Total Score</p>
            <p style={{ margin: 0, fontSize: "2rem", fontWeight: 800, lineHeight: 1 }}>{newReport.TotalScore}</p>
          </div>
        )}
      </div>



      {/* Areas List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {newReport.Areas.map((area, index) => (
          <div key={`areaIndex_${index}`} className="au-card" style={{ display: "flex", alignItems: "stretch" }}>

            {/* Left: Info */}
            <div className="au-card-body" style={{ flex: 1, padding: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <div style={{ width: 32, height: 32, borderRadius: "8px", background: "rgba(37,99,192,0.1)", color: "#2563C0", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.85rem" }}>
                  {index + 1}
                </div>
                <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f1c34" }}>{area.Name}</h3>
              </div>

              <div style={{ paddingLeft: "44px" }}>
                <ClientSubAreas subAreas={area.SubAreas} />
              </div>
            </div>

            {/* Right: Scoring Buttons */}
            <div style={{ width: "160px", background: "#F9FAFB", borderLeft: "1px solid #E5E7EB", display: "flex", flexDirection: "column" }}>

              <button
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  border: "none",
                  borderBottom: "1px solid #E5E7EB",
                  background: areaBasedScoringAreabuttonsClicked[index] === "up" ? "#16a34a" : "transparent",
                  color: areaBasedScoringAreabuttonsClicked[index] === "up" ? "#ffffff" : "#6B7280",
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
                onClick={() => handleButtonClick(index, "up")}
              >
                <ThumbsUpIcon />
                <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>PASS</span>
              </button>

              <button
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  border: "none",
                  background: areaBasedScoringAreabuttonsClicked[index] === "down" ? "#dc2626" : "transparent",
                  color: areaBasedScoringAreabuttonsClicked[index] === "down" ? "#ffffff" : "#6B7280",
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
                onClick={() => handleButtonClick(index, "down")}
              >
                <ThumbsDownIcon />
                <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>FAIL</span>
              </button>

            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

